import React, { ComponentProps, useContext } from 'react'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { Well } from '@pushly/aqe/lib/components'
import { PromptTreatmentEditor } from './prompt-treatment-editor'
import { PromptEditorTreatment } from '../types'
import { CreateSlidePromptRequest } from '@pushly/models/lib/structs/prompts/styles/slide/slide-prompt'
import { CreateNativePromptRequest } from '@pushly/models/lib/structs/prompts/styles/native/native-prompt'
import { PromptStyle } from '@pushly/models/lib/enums/prompts/prompt-style'
import { toPrompt } from '../helpers'
import { CreateCustomPromptRequest } from '@pushly/models/lib/structs'
import { hasClassNameOrParent } from '../../../../_utils/events'
import { PromptEditorDispatchContext } from '../reducer'
import { PromptEditorContext } from '../context'
import { Switch, Tabs } from 'antd'

type Writable<T> = {
    -readonly [P in keyof T]: T[P] extends object
        ? T[P] extends Function
            ? T[P]
            : T[P] extends Array<infer U>
            ? Array<Writable<U>>
            : Writable<T[P]>
        : T[P]
}

export const PromptTreatmentsManager = () => {
    const dispatch = useContext(PromptEditorDispatchContext)
    const { loading, prompts, selectedPrompt, selectedPromptIdx, isAbTestEnabled } = useContext(PromptEditorContext)

    const hasMaxTreatments = loading || prompts.length >= 4
    const hasMultipleTreatments = loading || prompts.length > 1
    const wellProps: Writable<ComponentProps<typeof Well>> = {}

    if (isAbTestEnabled && prompts?.length) {
        wellProps.headerExtension = (
            <div className="well-tab-list">
                {prompts.map((prompt, idx) => {
                    const isSelected = idx === selectedPromptIdx

                    return (
                        <div
                            key={idx}
                            className={`well-tab${isSelected ? ' active' : ''}`}
                            onClick={(ev) => {
                                // prevent selection when target is removal
                                if (hasClassNameOrParent(ev, 'well-tab-close')) {
                                    return
                                }

                                dispatch({ type: 'set-selected-treatment-idx', idx })
                            }}
                        >
                            {hasMultipleTreatments && (
                                <span
                                    className="well-tab-close"
                                    onClick={() => {
                                        dispatch({ type: 'rem-prompt-idx', idx })
                                    }}
                                    role="button"
                                    aria-label={`Remove variant ${idx + 1}`}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            dispatch({ type: 'rem-prompt-idx', idx })
                                        }
                                    }}
                                >
                                    <CloseOutlined />
                                </span>
                            )}
                            <span className="well-tab-name">
                                <span>Variant {idx + 1}</span>
                                <span>({prompt.weight || 0}%)</span>
                            </span>
                        </div>
                    )
                })}
                {!hasMaxTreatments && (
                    <div
                        className="well-tab well-tab-new"
                        onClick={() => {
                            const currPrompt = selectedPrompt!
                            let nextPrompt: PromptEditorTreatment

                            switch (currPrompt.style) {
                                case PromptStyle.SLIDE: {
                                    nextPrompt = toPrompt(CreateSlidePromptRequest, currPrompt)
                                    break
                                }
                                case PromptStyle.PUBLISHER_CUSTOM: {
                                    nextPrompt = toPrompt(CreateCustomPromptRequest, currPrompt)
                                    break
                                }
                                case PromptStyle.NATIVE:
                                default: {
                                    nextPrompt = toPrompt(CreateNativePromptRequest, currPrompt)
                                    break
                                }
                            }

                            const currTtlWeight = prompts.map(({ weight }) => weight ?? 0).reduce((a, b) => a + b, 0)

                            nextPrompt.weight = currTtlWeight >= 100 ? 0 : 100 - currTtlWeight

                            dispatch({ type: 'add-prompt', prompt: nextPrompt })
                        }}
                    >
                        <span>
                            <PlusOutlined />
                        </span>
                    </div>
                )}
            </div>
        )
    }

    return (
        <Well
            className="composite-prompt-well treatment-well nested"
            title="Appearance & Behavior"
            footerMode="hidden"
            styles={
                isAbTestEnabled
                    ? {
                          body: { padding: 0 },
                      }
                    : undefined
            }
            actions={
                <label>
                    <span className="label-with-component-r">A/B Testing</span>
                    <Switch
                        checked={isAbTestEnabled}
                        onChange={(enabled) => {
                            dispatch({ type: 'set-is-ab-test-enabled', enabled })
                        }}
                        aria-label="Enable A/B Testing"
                    />
                </label>
            }
        >
            {isAbTestEnabled && prompts?.length ? (
                <Tabs
                    styles={{
                        content: { padding: 24 },
                    }}
                    className="treatment-tabs"
                    classNames={{
                        item: 'prompt-treatment-tab',
                    }}
                    type="editable-card"
                    activeKey={String(selectedPromptIdx)}
                    items={prompts?.map((prompt, idx) => ({
                        key: String(idx),
                        label: (
                            <>
                                <span>Variant {idx + 1}</span>{' '}
                                <span className="font-size--s fg--color--gray-2">({prompt.weight}%)</span>
                            </>
                        ),
                        children: <PromptTreatmentEditor />,
                        closable: idx !== 0,
                    }))}
                    hideAdd={hasMaxTreatments}
                    onEdit={(key, action) => {
                        if (action === 'remove') {
                            dispatch({ type: 'rem-prompt-idx', idx: Number(key) })
                        } else {
                            const currPrompt = selectedPrompt!
                            let nextPrompt: PromptEditorTreatment

                            switch (currPrompt.style) {
                                case PromptStyle.SLIDE: {
                                    nextPrompt = toPrompt(CreateSlidePromptRequest, currPrompt)
                                    break
                                }
                                case PromptStyle.PUBLISHER_CUSTOM: {
                                    nextPrompt = toPrompt(CreateCustomPromptRequest, currPrompt)
                                    break
                                }
                                case PromptStyle.NATIVE:
                                default: {
                                    nextPrompt = toPrompt(CreateNativePromptRequest, currPrompt)
                                    break
                                }
                            }

                            const currTtlWeight = prompts?.map(({ weight }) => weight ?? 0).reduce((a, b) => a + b, 0)

                            nextPrompt.weight = currTtlWeight >= 100 ? 0 : 100 - currTtlWeight

                            dispatch({ type: 'add-prompt', prompt: nextPrompt })
                        }
                    }}
                    onChange={(key) => {
                        dispatch({ type: 'set-selected-treatment-idx', idx: Number(key) })
                    }}
                />
            ) : (
                <PromptTreatmentEditor />
            )}
        </Well>
    )
}
